import { getFullSelectedIds } from '@aibees/elephant/feature/search-table-company/utils';
import { useSearchTypeFromRoute } from '@aibees/elephant/hooks';
import { PEOPLE_COMPANY_MAP_EXTRA_PARAM } from '@aibees/elephant/hooks/use-browse-search-ids';
import { useHandleFindButton } from '@aibees/elephant/hooks/use-handle-find-button';
import { SearchType } from '@aibees/elephant/types';
import { Icon } from '@aibees/shared/components';
import { useFullScreenLoadingContext } from '@aibees/shared/context';
import { useGetBrowseIdsApiData } from '@aibees/shared/features/advance-table/components/table';
import { useAdvanceTableContext } from '@aibees/shared/features/advance-table/context';
import { classnames } from '@aibees/shared/utils';
import { Button } from '@mui/material';
import { uniq } from 'lodash-es';
import { memo, useCallback, useMemo } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { FindButtonProps as Props, PersonRowLike } from './types';

function getCompanyIdFromRow(row?: PersonRowLike): string | null | undefined {
  return row?.company?.id ?? row?.positionGroups?.[0]?.company?.id;
}

export const FindButton = memo(
  ({ className, disabled, isSmall, table }: Props) => {
    const type = useSearchTypeFromRoute();
    const { setTitle: setLoadingScreenTitle, state: loadingScreenState } =
      useFullScreenLoadingContext();
    const { browseData } = useAdvanceTableContext();
    const title =
      type === SearchType.People
        ? 'Find Companies which the selected people are working in.'
        : 'Find People that are working in the selected companies.';
    const getSelectedIdsApi = useGetBrowseIdsApiData(table);
    const selectedIds = useMemo(() => {
      return getFullSelectedIds(getSelectedIdsApi?.data ?? [], table);
    }, [getSelectedIdsApi?.data, table]);

    const { createSearchListAndRedirect } = useHandleFindButton({ type });

    const isDisabled = disabled || getSelectedIdsApi?.isFetching;

    const onClick = useCallback(() => {
      loadingScreenState.onTrue();
      setLoadingScreenTitle('Redirecting...');
      if (type !== SearchType.People) {
        createSearchListAndRedirect(selectedIds);
        return;
      }
      const peopleCompanyMap = (table?.selection.extraParam?.[
        PEOPLE_COMPANY_MAP_EXTRA_PARAM
      ] ?? {}) as Record<string, string>;
      const itemsById = new Map<string, PersonRowLike>(
        ((table?.selection.items ?? []) as PersonRowLike[]).map((item) => [
          item.id,
          item,
        ])
      );
      const topById = new Map<string, PersonRowLike>(
        ((browseData.data?.top ?? []) as PersonRowLike[]).map((item) => [
          item.id,
          item,
        ])
      );
      // Priority: fresh on-screen row data first (selection.items in PAGE /
      // manual modes, browseData.data?.top for sticky rows), then fall back to
      // peopleCompanyMap. The map is the only source for virtual-mode IDs that
      // aren't loaded as rows, but it can outlive a filter change so we don't
      // want it to win over what the user is currently looking at.
      const companyIds = uniq(
        selectedIds
          .map(
            (pid) =>
              getCompanyIdFromRow(itemsById.get(pid)) ??
              getCompanyIdFromRow(topById.get(pid)) ??
              peopleCompanyMap[pid]
          )
          .filter((cid): cid is string => Boolean(cid))
      );
      createSearchListAndRedirect(companyIds);
    }, [
      browseData.data?.top,
      createSearchListAndRedirect,
      loadingScreenState,
      selectedIds,
      setLoadingScreenTitle,
      table?.selection.extraParam,
      table?.selection.items,
      type,
    ]);

    return (
      <Styled.Wrapper
        className={classnames('FindButton', className)}
        sx={SX.root}
        title={isDisabled ? title : undefined}
        placement="top"
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={
            <Icon
              name={type === SearchType.People ? 'business' : 'people-outline'}
              size={18}
            />
          }
          onClick={onClick}
          disabled={isDisabled}
        >
          {!isSmall &&
            (type === SearchType.People ? 'Find Companies' : 'Find People')}
        </Button>
      </Styled.Wrapper>
    );
  }
);

FindButton.displayName = 'FindButton';
